import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronDown, ChevronRight, Save, RefreshCw, FileText, Search, AlertCircle, CheckCircle, Eye } from 'lucide-react';

/**
 * TaxonomyEditor — UI admin para editar el árbol de categorías + descripciones + reglas + ejemplos.
 *
 * 3 tabs:
 *  - Editor: edición por nodos (cat / sub / seg). Por nodo: descripción, keywords, reglas, ejemplos.
 *  - Discovery: scan catalog DB para valores out-of-tree con counts.
 *  - Preview: ver prompt renderizado + diff vs versión activa.
 *
 * Solo superadmin puede guardar (PUT /api/clients/:id/taxonomy).
 * Versioning automático: cada save bump version (v6.8 → v6.9). Activación explícita.
 */

interface Taxonomy {
  version: string;
  clientId: string;
  active: boolean;
  systemPrompt: string;
  guardrails: string[];
  globalRules: Record<string, string[] | string>;
  globalExamples: any[];
  tree: TreeCategory[];
  notes?: string[];
  createdAt?: string;
  parentVersion?: string;
  outputInstructions?: string;
}

interface TreeCategory {
  id?: string;
  value: string;
  macroCategoria?: string;
  description?: string;
  subcategories: TreeSubcategory[];
}

interface TreeSubcategory {
  id?: string;
  value: string;
  macroCategoria?: string;
  udm: string;
  description?: string;
  keywords?: string[];
  rules?: string[];
  guardrails?: string[];
  segments: TreeSegment[];
  examples?: TaxonomyExample[];
}

interface TreeSegment {
  id?: string;
  value: string;
  description?: string;
  keywords?: string[];
}

interface TaxonomyExample {
  title: string;
  output: any;
  label?: string;
  sourceBrand?: string;
}

interface Props {
  clientId: string;
  onClose?: () => void;
}

export default function TaxonomyEditor({ clientId, onClose }: Props) {
  const { userData } = useAuth();
  const isSuperadmin = userData?.user_type === 'superadmin';
  const [tab, setTab] = useState<'editor' | 'tree' | 'mapping' | 'discovery' | 'preview'>('editor');
  // Vista árbol (Miller columns): navegación por selección
  const [treeSelCat, setTreeSelCat] = useState<number | null>(null);
  const [treeSelSub, setTreeSelSub] = useState<number | null>(null);
  const [treeHover, setTreeHover] = useState<{ level: 'cat' | 'sub' | 'seg'; ci: number; si?: number; sgi?: number } | null>(null);
  const [taxonomy, setTaxonomy] = useState<Taxonomy | null>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());
  const [expandedSubs, setExpandedSubs] = useState<Set<string>>(new Set());
  const [discovery, setDiscovery] = useState<any>(null);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // Conexión de columnas (mapping tree-level → catalog column)
  const [mapping, setMapping] = useState<any>(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingSaving, setMappingSaving] = useState(false);
  const [mappingSel, setMappingSel] = useState<Record<number, string>>({});
  const [mappingErrors, setMappingErrors] = useState<string[] | null>(null);

  // ---------- Loaders ----------
  const loadTaxonomy = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get(`/api/clients/${clientId}/taxonomy`);
      setTaxonomy(res.data);
      // Expandir todas las cats por default
      setExpandedCats(new Set(res.data.tree.map((c: TreeCategory) => c.value)));
      setHasChanges(false);
    } catch (e: any) {
      if (e.response?.status === 404) {
        setError('Este cliente todavía no tiene taxonomía configurada. Hay que sembrarla (migración) para empezar a editar el árbol. Clientes con taxonomía hoy: Samsung y LG.');
      } else {
        setError(e.response?.data?.error || e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadVersions = useCallback(async () => {
    try {
      const res = await api.get(`/api/clients/${clientId}/taxonomy/versions`);
      setVersions(res.data.versions || []);
    } catch (e) { /* silent */ }
  }, [clientId]);

  useEffect(() => {
    loadTaxonomy();
    loadVersions();
  }, [loadTaxonomy, loadVersions]);

  // ---------- Mutators ----------
  function setTreeField(catIdx: number, subIdx: number | null, segIdx: number | null, field: string, value: any) {
    if (!taxonomy) return;
    const next = { ...taxonomy, tree: [...taxonomy.tree] };
    if (subIdx === null) {
      next.tree[catIdx] = { ...next.tree[catIdx], [field]: value };
    } else if (segIdx === null) {
      const subs = [...next.tree[catIdx].subcategories];
      subs[subIdx] = { ...subs[subIdx], [field]: value };
      next.tree[catIdx] = { ...next.tree[catIdx], subcategories: subs };
    } else {
      const subs = [...next.tree[catIdx].subcategories];
      const segs = [...subs[subIdx].segments];
      segs[segIdx] = { ...segs[segIdx], [field]: value };
      subs[subIdx] = { ...subs[subIdx], segments: segs };
      next.tree[catIdx] = { ...next.tree[catIdx], subcategories: subs };
    }
    setTaxonomy(next);
    setHasChanges(true);
  }

  function addSegment(catIdx: number, subIdx: number) {
    if (!taxonomy) return;
    const newSeg: TreeSegment = { value: 'Nuevo segmento', description: '' };
    const next = { ...taxonomy, tree: [...taxonomy.tree] };
    const subs = [...next.tree[catIdx].subcategories];
    subs[subIdx] = { ...subs[subIdx], segments: [...subs[subIdx].segments, newSeg] };
    next.tree[catIdx] = { ...next.tree[catIdx], subcategories: subs };
    setTaxonomy(next);
    setHasChanges(true);
  }

  function removeSegment(catIdx: number, subIdx: number, segIdx: number) {
    if (!taxonomy) return;
    const next = { ...taxonomy, tree: [...taxonomy.tree] };
    const subs = [...next.tree[catIdx].subcategories];
    const segs = subs[subIdx].segments.filter((_, i) => i !== segIdx);
    subs[subIdx] = { ...subs[subIdx], segments: segs };
    next.tree[catIdx] = { ...next.tree[catIdx], subcategories: subs };
    setTaxonomy(next);
    setHasChanges(true);
  }

  function bumpVersion(v: string) {
    const parts = v.split('.').map(n => parseInt(n) || 0);
    parts[parts.length - 1] = (parts[parts.length - 1] || 0) + 1;
    return parts.join('.');
  }

  async function saveAsNewVersion() {
    if (!taxonomy) return;
    setSaving(true); setError(null);
    try {
      const newVersion = bumpVersion(taxonomy.version);
      const newTaxonomy = { ...taxonomy, version: newVersion, parentVersion: taxonomy.version, active: false };
      await api.put(`/api/clients/${clientId}/taxonomy`, newTaxonomy);
      setHasChanges(false);
      await loadVersions();
      alert(`Versión ${newVersion} guardada como draft (no activa).`);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function activateVersion(version: string) {
    if (!window.confirm(`Activar versión ${version}? Las otras versiones quedarán inactivas.`)) return;
    try {
      await api.post(`/api/clients/${clientId}/taxonomy/activate`, { version });
      await loadTaxonomy();
      await loadVersions();
      alert(`Versión ${version} activada.`);
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    }
  }

  async function runDiscovery() {
    setDiscoveryLoading(true);
    try {
      // Forzar x-client-id al cliente de la ruta: el interceptor manda 'admin-mode'
      // cuando el superadmin administra clientes, lo que hace que req.db resuelva la
      // DB default (sin tabla `catalog` del cliente) => 500. Ver T-F4.
      const res = await api.post(`/api/clients/${clientId}/taxonomy/discovery`, {
        columns: ['catalog_category', 'subcategory', 'segmento'],
      }, { headers: { 'x-client-id': clientId } });
      setDiscovery(res.data);
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setDiscoveryLoading(false);
    }
  }

  async function generatePromptPreview() {
    if (!taxonomy) return;
    setPreviewLoading(true);
    try {
      const res = await api.post(`/api/clients/${clientId}/taxonomy/preview-prompt`, { taxonomy });
      setPreviewPrompt(res.data.promptText);
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    } finally {
      setPreviewLoading(false);
    }
  }

  // ---------- Structural CRUD (DB-backed nodes) ----------
  const hdr = { headers: { 'x-client-id': clientId } };

  async function addCategory() {
    const value = window.prompt('Nombre de la nueva categoría')?.trim();
    if (!value) return;
    try {
      await api.post(`/api/clients/${clientId}/taxonomy/node`, { parentId: null, level: 1, value }, hdr);
      await loadTaxonomy();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  }

  async function addSubcategory(catId: string) {
    const value = window.prompt('Nombre de la nueva subcategoría')?.trim();
    if (!value) return;
    try {
      await api.post(`/api/clients/${clientId}/taxonomy/node`, { parentId: catId, level: 2, value, fields: { udm: 'Otros' } }, hdr);
      await loadTaxonomy();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  }

  async function addSegmentNode(subId: string) {
    const value = window.prompt('Nombre del nuevo segmento')?.trim();
    if (!value) return;
    try {
      await api.post(`/api/clients/${clientId}/taxonomy/node`, { parentId: subId, level: 3, value }, hdr);
      await loadTaxonomy();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  }

  async function deleteNodeOp(nodeId: string, label: string) {
    try {
      await api.delete(`/api/clients/${clientId}/taxonomy/node/${nodeId}?strategy=block`, hdr);
      await loadTaxonomy();
    } catch (e: any) {
      const data = e.response?.data;
      if (e.response?.status === 409 && data?.code === 'HAS_CHILDREN') {
        const ok = window.confirm(`"${label}" tiene ${data.childrenCount} hijos. OK = borrar todo el subárbol (cascade). Cancelar = abortar.`);
        if (!ok) return;
        try {
          await api.delete(`/api/clients/${clientId}/taxonomy/node/${nodeId}?strategy=cascade`, hdr);
          await loadTaxonomy();
        } catch (e2: any) {
          setError(e2.response?.data?.error || e2.message);
        }
        return;
      }
      setError(data?.error || e.message);
    }
  }

  async function renameNodeOp(nodeId: string, oldValue: string, newValue: string) {
    const nv = (newValue || '').trim();
    if (!nv || nv === oldValue) return;
    try {
      const impact = await api.post(`/api/clients/${clientId}/taxonomy/rename-impact`, { nodeId, newValue: nv }, hdr);
      const { column, affectedCount, propagates } = impact.data;
      let propagate = false;
      if (propagates && affectedCount > 0) {
        propagate = window.confirm(`Renombrar "${oldValue}" → "${nv}" también actualizará ${affectedCount} filas de CATALOG (columna ${column}). ¿Continuar?`);
      }
      await api.patch(`/api/clients/${clientId}/taxonomy/node/${nodeId}`, { value: nv, propagate }, hdr);
      await loadTaxonomy();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  }

  async function reparentNode(nodeId: string, newParentId: string) {
    try {
      await api.post(`/api/clients/${clientId}/taxonomy/node/${nodeId}/reparent`, { newParentId }, hdr);
      await loadTaxonomy();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    }
  }

  // ---------- Mapping (conexión de columnas) ----------
  const loadMapping = useCallback(async () => {
    setMappingLoading(true); setMappingErrors(null);
    try {
      const res = await api.get(`/api/clients/${clientId}/taxonomy/mapping`, { headers: { 'x-client-id': clientId } });
      setMapping(res.data);
      const sel: Record<number, string> = {};
      (res.data.mapping || []).forEach((m: any) => { sel[m.level] = m.column; });
      setMappingSel(sel);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setMappingLoading(false);
    }
  }, [clientId]);

  async function saveMapping() {
    setMappingSaving(true); setMappingErrors(null);
    try {
      const built: { level: number; column: string }[] = [];
      for (const level of [1, 2, 3]) {
        const column = mappingSel[level];
        if (!column) break; // mantener contiguo desde nivel 1
        built.push({ level, column });
      }
      await api.put(`/api/clients/${clientId}/taxonomy/mapping`, { mapping: built }, { headers: { 'x-client-id': clientId } });
      await loadMapping();
      alert('Conexión guardada');
    } catch (e: any) {
      const data = e.response?.data;
      if (e.response?.status === 400 && Array.isArray(data?.errors)) {
        setMappingErrors(data.errors);
      } else {
        setError(data?.error || e.message);
      }
    } finally {
      setMappingSaving(false);
    }
  }

  // ---------- Render ----------
  if (loading) return <div className="p-6 text-center text-gray-500">Cargando taxonomía...</div>;
  if (!taxonomy) return <div className="p-6 text-center text-red-600">No hay taxonomía configurada para este cliente.</div>;

  const totalCats = taxonomy.tree.length;
  const totalSubs = taxonomy.tree.reduce((sum, c) => sum + c.subcategories.length, 0);
  const totalSegs = taxonomy.tree.reduce((sum, c) => sum + c.subcategories.reduce((ss, s) => ss + s.segments.length, 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4 border-b pb-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="w-5 h-5" /> Árbol de Categorías
            <span className={`ml-2 px-2 py-0.5 rounded text-xs font-mono ${taxonomy.active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
              v{taxonomy.version} {taxonomy.active ? '· ACTIVA' : '· draft'}
            </span>
          </h2>
          <div className="text-xs text-gray-500 mt-0.5">{totalCats} cats · {totalSubs} subs · {totalSegs} segs</div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && <span className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Cambios sin guardar</span>}
          {isSuperadmin && (
            <button onClick={saveAsNewVersion} disabled={!hasChanges || saving} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1">
              <Save className="w-4 h-4" /> {saving ? 'Guardando...' : `Guardar como v${bumpVersion(taxonomy.version)}`}
            </button>
          )}
          <button onClick={() => loadTaxonomy()} className="text-sm px-2 py-1 text-gray-600 hover:bg-gray-100 rounded flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Recargar
          </button>
          {onClose && <button onClick={onClose} className="text-gray-500 hover:text-gray-700 px-2">✕</button>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        {(['editor', 'tree', 'mapping', 'discovery', 'preview'] as const).map(t => (
          <button
            key={t}
            onClick={() => { setTab(t); if (t === 'discovery' && !discovery) runDiscovery(); if (t === 'preview' && !previewPrompt) generatePromptPreview(); if (t === 'mapping' && !mapping) loadMapping(); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            {t === 'editor' && 'Editor'}
            {t === 'tree' && 'Vista árbol'}
            {t === 'mapping' && 'Conexión de columnas'}
            {t === 'discovery' && 'Discovery'}
            {t === 'preview' && 'Preview prompt'}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-50 text-red-800 p-3 rounded mb-3 text-sm">{error}</div>}

      {tab === 'editor' && (
        <div className="space-y-3">
          {/* Lista versiones (solo lectura) */}
          {versions.length > 1 && (
            <div className="bg-gray-50 p-2 rounded text-xs flex items-center gap-3 flex-wrap">
              <span className="font-semibold">Versiones:</span>
              {versions.map(v => (
                <span key={v.version} className="flex items-center gap-1">
                  <code className={v.active ? 'font-bold text-green-700' : 'text-gray-600'}>v{v.version}</code>
                  {!v.active && isSuperadmin && (
                    <button onClick={() => activateVersion(v.version)} className="text-blue-600 hover:underline">activar</button>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Alta de categoría */}
          {isSuperadmin && (
            <div className="flex justify-end">
              <button onClick={addCategory} className="text-xs text-blue-600 hover:underline font-medium">+ agregar categoría</button>
            </div>
          )}

          {/* Árbol */}
          {taxonomy.tree.map((cat, ci) => (
            <div key={cat.value} className="border rounded">
              <div className="bg-slate-100 px-3 py-2 flex items-center gap-2 cursor-pointer" onClick={() => {
                const next = new Set(expandedCats);
                next.has(cat.value) ? next.delete(cat.value) : next.add(cat.value);
                setExpandedCats(next);
              }}>
                {expandedCats.has(cat.value) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <input
                  value={cat.value}
                  onChange={e => setTreeField(ci, null, null, 'value', e.target.value)}
                  onFocus={e => { e.currentTarget.dataset.orig = e.currentTarget.value; }}
                  onBlur={e => { if (cat.id) renameNodeOp(cat.id, e.currentTarget.dataset.orig || '', e.currentTarget.value); }}
                  className="font-bold bg-transparent border-b border-transparent hover:border-slate-400 focus:border-blue-500 outline-none"
                  onClick={e => e.stopPropagation()}
                  disabled={!isSuperadmin}
                />
                <span className="text-xs text-gray-500">({cat.subcategories.length} subs)</span>
                {isSuperadmin && cat.id && (
                  <span className="flex items-center gap-2 ml-auto" onClick={e => e.stopPropagation()}>
                    <button onClick={() => addSubcategory(cat.id!)} className="text-xs text-blue-600 hover:underline">+ sub</button>
                    <button onClick={() => deleteNodeOp(cat.id!, cat.value)} className="text-red-600 text-xs">✕</button>
                  </span>
                )}
              </div>
              {expandedCats.has(cat.value) && (
                <div className="p-3 space-y-3">
                  <label className="block text-xs">
                    <span className="text-gray-700">Descripción de la categoría</span>
                    <textarea
                      value={cat.description || ''}
                      onChange={e => setTreeField(ci, null, null, 'description', e.target.value)}
                      className="w-full border rounded p-1 text-sm mt-0.5"
                      rows={2}
                      disabled={!isSuperadmin}
                    />
                  </label>

                  {cat.subcategories.map((sub, si) => {
                    const subKey = `${cat.value}::${sub.value}`;
                    return (
                      <div key={subKey} className="border-l-4 border-blue-300 pl-3 ml-2">
                        <div className="flex items-center gap-2 cursor-pointer" onClick={() => {
                          const next = new Set(expandedSubs);
                          next.has(subKey) ? next.delete(subKey) : next.add(subKey);
                          setExpandedSubs(next);
                        }}>
                          {expandedSubs.has(subKey) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          <input
                            value={sub.value}
                            onChange={e => setTreeField(ci, si, null, 'value', e.target.value)}
                            onFocus={e => { e.currentTarget.dataset.orig = e.currentTarget.value; }}
                            onBlur={e => { if (sub.id) renameNodeOp(sub.id, e.currentTarget.dataset.orig || '', e.currentTarget.value); }}
                            className="font-semibold bg-transparent border-b border-transparent hover:border-slate-400 focus:border-blue-500 outline-none text-sm"
                            onClick={e => e.stopPropagation()}
                            disabled={!isSuperadmin}
                          />
                          {isSuperadmin && sub.id && (
                            <button onClick={e => { e.stopPropagation(); deleteNodeOp(sub.id!, sub.value); }} className="text-red-600 text-xs">✕</button>
                          )}
                          <span className="text-xs text-gray-500">UDM:</span>
                          <input
                            value={sub.udm}
                            onChange={e => setTreeField(ci, si, null, 'udm', e.target.value)}
                            className="text-xs bg-transparent border-b border-slate-300 w-20 px-1 outline-none focus:border-blue-500"
                            onClick={e => e.stopPropagation()}
                            disabled={!isSuperadmin}
                          />
                          <span className="text-xs text-gray-500">({sub.segments.length} segs)</span>
                        </div>

                        {expandedSubs.has(subKey) && (
                          <div className="mt-2 space-y-2">
                            <label className="block text-xs">
                              <span className="text-gray-700">Descripción</span>
                              <textarea
                                value={sub.description || ''}
                                onChange={e => setTreeField(ci, si, null, 'description', e.target.value)}
                                className="w-full border rounded p-1 text-xs"
                                rows={2}
                                disabled={!isSuperadmin}
                              />
                            </label>
                            <label className="block text-xs">
                              <span className="text-gray-700">Keywords (coma-separadas)</span>
                              <input
                                value={(sub.keywords || []).join(', ')}
                                onChange={e => setTreeField(ci, si, null, 'keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                className="w-full border rounded p-1 text-xs"
                                disabled={!isSuperadmin}
                              />
                            </label>
                            <label className="block text-xs">
                              <span className="text-gray-700">Reglas (1 por línea)</span>
                              <textarea
                                value={(sub.rules || []).join('\n')}
                                onChange={e => setTreeField(ci, si, null, 'rules', e.target.value.split('\n').filter(Boolean))}
                                className="w-full border rounded p-1 text-xs font-mono"
                                rows={Math.max(3, (sub.rules || []).length)}
                                disabled={!isSuperadmin}
                              />
                            </label>

                            <div>
                              <div className="text-xs text-gray-700 mb-1 flex items-center justify-between">
                                <span>Segmentos:</span>
                                {isSuperadmin && (
                                  <button onClick={() => sub.id ? addSegmentNode(sub.id) : addSegment(ci, si)} className="text-xs text-blue-600 hover:underline">+ agregar segmento</button>
                                )}
                              </div>
                              {sub.segments.map((seg, sgi) => (
                                <div key={`${seg.value}-${sgi}`} className="flex items-start gap-2 bg-slate-50 p-2 rounded mb-1">
                                  <input
                                    value={seg.value}
                                    onChange={e => setTreeField(ci, si, sgi, 'value', e.target.value)}
                                    className="text-xs font-semibold bg-white border rounded px-1 py-0.5 w-40"
                                    disabled={!isSuperadmin}
                                  />
                                  <input
                                    value={seg.description || ''}
                                    onChange={e => setTreeField(ci, si, sgi, 'description', e.target.value)}
                                    placeholder="Descripción"
                                    className="flex-1 text-xs bg-white border rounded px-1 py-0.5"
                                    disabled={!isSuperadmin}
                                  />
                                  <input
                                    value={(seg.keywords || []).join(', ')}
                                    onChange={e => setTreeField(ci, si, sgi, 'keywords', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                    placeholder="keywords (coma)"
                                    className="w-48 text-xs bg-white border rounded px-1 py-0.5"
                                    disabled={!isSuperadmin}
                                  />
                                  {isSuperadmin && (
                                    <button onClick={() => removeSegment(ci, si, sgi)} className="text-red-600 text-xs">✕</button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          {/* Guardrails (read/edit) */}
          <details className="border rounded">
            <summary className="bg-slate-100 px-3 py-2 cursor-pointer font-semibold text-sm">Guardrails globales ({taxonomy.guardrails.length})</summary>
            <div className="p-3">
              <textarea
                value={(taxonomy.guardrails || []).join('\n')}
                onChange={e => { setTaxonomy({ ...taxonomy, guardrails: e.target.value.split('\n').filter(Boolean) }); setHasChanges(true); }}
                className="w-full border rounded p-2 text-xs font-mono"
                rows={Math.max(5, taxonomy.guardrails.length)}
                disabled={!isSuperadmin}
              />
            </div>
          </details>
        </div>
      )}

      {tab === 'tree' && (() => {
        // Nodo activo = hover si existe, si no la selección actual (sub > cat)
        const active = treeHover
          ? treeHover
          : (treeSelCat !== null && treeSelSub !== null)
            ? { level: 'sub' as const, ci: treeSelCat, si: treeSelSub }
            : (treeSelCat !== null)
              ? { level: 'cat' as const, ci: treeSelCat }
              : null;
        const cat = active ? taxonomy.tree[active.ci] : null;
        const sub = cat && active && 'si' in active && active.si != null ? cat.subcategories[active.si] : null;
        const seg = sub && active && active.level === 'seg' && active.sgi != null ? sub.segments[active.sgi] : null;
        const detailTitle = seg ? seg.value : sub ? sub.value : cat ? cat.value : null;
        const detailLevel = seg ? 'Segmento' : sub ? 'Subcategoría' : cat ? 'Categoría' : null;

        return (
          <div>
            <div className="text-sm text-gray-700 mb-3">
              Navegá el árbol por columnas: <b>Categoría → Subcategoría → Segmento</b>. Hacé hover o click en cualquier nodo para ver su detalle. Read-only.
            </div>
            <div className="grid grid-cols-4 gap-3" onMouseLeave={() => setTreeHover(null)}>
              {/* Columna 1: Categorías */}
              <div className="border rounded overflow-hidden">
                <div className="bg-slate-100 px-2 py-1.5 text-xs font-semibold text-gray-700">Categorías ({taxonomy.tree.length})</div>
                <div className="max-h-[520px] overflow-auto">
                  {taxonomy.tree.map((c, ci) => (
                    <div
                      key={c.value}
                      onClick={() => { setTreeSelCat(ci); setTreeSelSub(null); }}
                      onMouseEnter={() => setTreeHover({ level: 'cat', ci })}
                      className={`px-2 py-1.5 text-sm cursor-pointer flex items-center justify-between border-b last:border-b-0 ${treeSelCat === ci ? 'bg-blue-50 text-blue-800 font-medium' : 'hover:bg-slate-50'}`}
                    >
                      <span className="truncate">{c.value}</span>
                      <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0">{c.subcategories.length}<ChevronRight className="w-3 h-3" /></span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Columna 2: Subcategorías */}
              <div className="border rounded overflow-hidden">
                <div className="bg-slate-100 px-2 py-1.5 text-xs font-semibold text-gray-700">
                  Subcategorías {treeSelCat !== null ? `(${taxonomy.tree[treeSelCat].subcategories.length})` : ''}
                </div>
                <div className="max-h-[520px] overflow-auto">
                  {treeSelCat === null ? (
                    <div className="p-2 text-xs text-gray-400">Elegí una categoría</div>
                  ) : taxonomy.tree[treeSelCat].subcategories.map((s, si) => (
                    <div
                      key={s.value}
                      onClick={() => setTreeSelSub(si)}
                      onMouseEnter={() => setTreeHover({ level: 'sub', ci: treeSelCat, si })}
                      className={`px-2 py-1.5 text-sm cursor-pointer flex items-center justify-between border-b last:border-b-0 ${treeSelSub === si ? 'bg-blue-50 text-blue-800 font-medium' : 'hover:bg-slate-50'}`}
                    >
                      <span className="truncate">{s.value}</span>
                      <span className="flex items-center gap-1 text-xs text-gray-400 shrink-0">{s.segments.length}<ChevronRight className="w-3 h-3" /></span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Columna 3: Segmentos */}
              <div className="border rounded overflow-hidden">
                <div className="bg-slate-100 px-2 py-1.5 text-xs font-semibold text-gray-700">
                  Segmentos {treeSelCat !== null && treeSelSub !== null ? `(${taxonomy.tree[treeSelCat].subcategories[treeSelSub].segments.length})` : ''}
                </div>
                <div className="max-h-[520px] overflow-auto">
                  {treeSelCat === null || treeSelSub === null ? (
                    <div className="p-2 text-xs text-gray-400">Elegí una subcategoría</div>
                  ) : taxonomy.tree[treeSelCat].subcategories[treeSelSub].segments.length === 0 ? (
                    <div className="p-2 text-xs text-gray-400">Sin segmentos</div>
                  ) : taxonomy.tree[treeSelCat].subcategories[treeSelSub].segments.map((sg, sgi) => (
                    <div
                      key={`${sg.value}-${sgi}`}
                      onMouseEnter={() => setTreeHover({ level: 'seg', ci: treeSelCat, si: treeSelSub, sgi })}
                      className="px-2 py-1.5 text-sm cursor-default border-b last:border-b-0 hover:bg-slate-50 truncate"
                    >
                      {sg.value}
                    </div>
                  ))}
                </div>
              </div>

              {/* Columna 4: Detalle del nodo activo */}
              <div className="border rounded overflow-hidden bg-slate-50">
                <div className="bg-slate-100 px-2 py-1.5 text-xs font-semibold text-gray-700">Detalle</div>
                <div className="p-3 max-h-[520px] overflow-auto text-xs space-y-2">
                  {!detailTitle ? (
                    <div className="text-gray-400">Hover o click en un nodo para ver su detalle.</div>
                  ) : (
                    <>
                      <div>
                        <span className="inline-block px-1.5 py-0.5 rounded bg-slate-200 text-gray-700 text-[10px] uppercase tracking-wide">{detailLevel}</span>
                        <div className="font-bold text-sm mt-1">{detailTitle}</div>
                      </div>
                      {sub && sub.udm && (
                        <div><span className="text-gray-500">UDM:</span> <code className="bg-white border rounded px-1">{sub.udm}</code></div>
                      )}
                      {(seg?.description || sub?.description || cat?.description) && (
                        <div>
                          <div className="text-gray-500 font-semibold">Descripción</div>
                          <div className="text-gray-800 whitespace-pre-wrap">{seg?.description || sub?.description || cat?.description}</div>
                        </div>
                      )}
                      {((seg?.keywords || sub?.keywords || []).length > 0) && (
                        <div>
                          <div className="text-gray-500 font-semibold">Keywords</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(seg?.keywords || sub?.keywords || []).map((k, i) => (
                              <span key={i} className="bg-white border rounded px-1 py-0.5">{k}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {!seg && sub && (sub.rules || []).length > 0 && (
                        <div>
                          <div className="text-gray-500 font-semibold">Reglas</div>
                          <ul className="list-disc pl-4 text-gray-800 space-y-0.5">
                            {(sub.rules || []).map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      )}
                      {!seg && sub && (sub.guardrails || []).length > 0 && (
                        <div>
                          <div className="text-gray-500 font-semibold">Guardrails</div>
                          <ul className="list-disc pl-4 text-gray-800 space-y-0.5">
                            {(sub.guardrails || []).map((g, i) => <li key={i}>{g}</li>)}
                          </ul>
                        </div>
                      )}
                      {!seg && sub && (sub.examples || []).length > 0 && (
                        <div>
                          <div className="text-gray-500 font-semibold">Ejemplos ({(sub.examples || []).length})</div>
                          <ul className="list-disc pl-4 text-gray-800 space-y-0.5">
                            {(sub.examples || []).slice(0, 5).map((ex, i) => <li key={i} className="truncate">{ex.title}</li>)}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {tab === 'mapping' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-700">
              Cada nivel del árbol se conecta a una columna materializada de CATALOG. Solo columnas ya pobladas pueden conectarse.
            </div>
            <button onClick={loadMapping} disabled={mappingLoading} className="text-sm px-2 py-1 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50">
              {mappingLoading ? 'Cargando...' : 'Recargar'}
            </button>
          </div>
          {mappingLoading && <div className="text-gray-500 text-sm">Cargando conexión...</div>}
          {mapping && (
            <div className="space-y-3">
              {mapping.hasTaxonomy === false && (
                <div className="bg-amber-50 text-amber-800 border border-amber-200 p-3 rounded text-sm">
                  Este cliente todavía no tiene taxonomía cargada. Abajo se muestra la <b>conexión sugerida por defecto</b>. Para editarla y guardarla, primero hay que sembrar la taxonomía del cliente (migración).
                </div>
              )}
              <div className="text-xs text-gray-600">
                Profundidad conectada: <b>{mapping.connectedDepth} niveles</b>
              </div>
              {mappingErrors && mappingErrors.length > 0 && (
                <div className="bg-red-50 text-red-800 p-3 rounded text-sm">
                  <div className="font-semibold mb-1">No se pudo guardar:</div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {mappingErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                </div>
              )}
              <table className="text-sm w-full border max-w-lg">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 w-24">Nivel</th>
                    <th className="text-left p-2">Columna de CATALOG</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3].map(level => (
                    <tr key={level} className="border-t">
                      <td className="p-2 tabular-nums">
                        {level} — {level === 1 ? 'Categoría' : level === 2 ? 'Subcategoría' : 'Segmento'}
                      </td>
                      <td className="p-2">
                        <select
                          value={mappingSel[level] || ''}
                          disabled={!isSuperadmin}
                          onChange={e => setMappingSel(prev => ({ ...prev, [level]: e.target.value }))}
                          className="border rounded p-1 text-sm w-full disabled:opacity-60"
                        >
                          <option value="">— (sin conectar)</option>
                          {(mapping.catalogColumns || []).map((col: string) => {
                            const materialized = (mapping.availableColumns || []).includes(col);
                            return (
                              <option key={col} value={col} disabled={!materialized}>
                                {col}{materialized ? '' : ' (no materializada)'}
                              </option>
                            );
                          })}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {isSuperadmin && (
                <button onClick={saveMapping} disabled={mappingSaving} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded disabled:opacity-50 flex items-center gap-1">
                  <Save className="w-4 h-4" /> {mappingSaving ? 'Guardando...' : 'Guardar conexión'}
                </button>
              )}
            </div>
          )}
          {!mapping && !mappingLoading && <div className="text-gray-500 text-sm">No hay datos de conexión aún.</div>}
        </div>
      )}

      {tab === 'discovery' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-700">
              Valores en <code>catalog</code> que NO están en el árbol actual.
              No se importan automáticamente — decidí explícitamente qué hacer con cada uno.
            </div>
            <button onClick={runDiscovery} disabled={discoveryLoading} className="bg-blue-600 text-white text-sm px-3 py-1 rounded disabled:opacity-50">
              {discoveryLoading ? 'Escaneando...' : 'Re-scan'}
            </button>
          </div>
          {!discovery && !discoveryLoading && <div className="text-gray-500 text-sm">No hay resultados aún.</div>}
          {discovery && (
            <div className="space-y-4">
              {Object.entries(discovery.results).map(([col, items]: any) => (
                <div key={col}>
                  <h4 className="font-semibold text-sm mb-1">{col} ({items.length} valores out-of-tree)</h4>
                  {items.length === 0 ? (
                    <div className="text-xs text-green-700 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Sin drift detectado en esta columna.</div>
                  ) : (
                    <table className="text-xs w-full border">
                      <thead className="bg-slate-100">
                        <tr><th className="text-left p-1">Valor</th><th className="text-right p-1">Items</th></tr>
                      </thead>
                      <tbody>
                        {items.slice(0, 30).map((it: any) => (
                          <tr key={it.value} className="border-t"><td className="p-1">{it.value}</td><td className="p-1 text-right tabular-nums">{it.count.toLocaleString('es-AR')}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'preview' && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-700">Prompt renderizado dinámicamente desde la taxonomía. Read-only.</div>
            <button onClick={generatePromptPreview} disabled={previewLoading} className="bg-blue-600 text-white text-sm px-3 py-1 rounded disabled:opacity-50">
              {previewLoading ? 'Generando...' : 'Re-generar'}
            </button>
          </div>
          {previewPrompt ? (
            <div>
              <div className="text-xs text-gray-500 mb-1">{previewPrompt.length.toLocaleString()} caracteres</div>
              <pre className="bg-gray-900 text-green-200 p-3 rounded text-xs overflow-auto max-h-[600px] whitespace-pre-wrap">{previewPrompt}</pre>
            </div>
          ) : (
            <div className="text-gray-500 text-sm">Click "Re-generar" para ver el prompt.</div>
          )}
        </div>
      )}
    </div>
  );
}
